import { useState, useMemo } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search as SearchIcon, Filter, X } from "lucide-react";
import { calculators, categories, subcategories, CalculatorData } from "@/data/calculators";
import Fuse from "fuse.js";
import { Link } from "react-router-dom";
import logoLight from "@/assets/logo-light_t.png";
import logoDark from "@/assets/logo-dark_t.png";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Search() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSubcategory, setSelectedSubcategory] = useState("all");

  // Configure Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(calculators, {
      keys: [
        { name: "title", weight: 2 },
        { name: "description", weight: 1.5 },
        { name: "tags", weight: 1 },
        { name: "subcategory", weight: 0.8 },
      ],
      threshold: 0.4,
      includeScore: true,
      minMatchCharLength: 2,
    });
  }, []);

  // Filter and search calculators
  const filteredCalculators = useMemo(() => {
    let results: CalculatorData[] = calculators;

    // Apply search query with fuzzy matching
    if (searchQuery.trim()) {
      const fuseResults = fuse.search(searchQuery);
      results = fuseResults.map(result => result.item);
    }

    // Apply category filter
    if (selectedCategory !== "all") {
      results = results.filter(calc => calc.category === selectedCategory);
    }

    // Apply subcategory filter
    if (selectedSubcategory !== "all") {
      results = results.filter(calc => calc.subcategory === selectedSubcategory);
    }

    return results;
  }, [searchQuery, selectedCategory, selectedSubcategory, fuse]);

  const availableSubcategories = useMemo(() => {
    if (selectedCategory === "all") return [];
    return subcategories[selectedCategory] || [];
  }, [selectedCategory]);

  const categoryColors = {
    physics: "from-primary/20 to-primary/5",
    chemistry: "from-accent/20 to-accent/5",
    math: "from-primary/15 to-accent/10",
    finance: "from-emerald-500/20 to-green-500/10",
    tools: "from-secondary/20 to-secondary/5",
  };

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Search Calculators</h1>
          <p className="text-muted-foreground">
            Find the perfect calculator for your needs from our extensive collection
          </p>
        </div>

        {/* Search and Filters */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SearchIcon className="h-5 w-5" />
              Search & Filter
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Search Input */}
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name, description, or tags (e.g., 'newton', 'ph', 'triangle')..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {availableSubcategories.length > 0 && (
                <div className="flex-1 min-w-[200px]">
                  <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Subcategory" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Subcategories</SelectItem>
                      {availableSubcategories.map((subcat) => (
                        <SelectItem key={subcat} value={subcat}>
                          {subcat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                  setSelectedSubcategory("all");
                }}
              >
                Clear Filters
              </Button>
            </div>

            {/* Active Filters Display */}
            {(searchQuery || selectedCategory !== "all" || selectedSubcategory !== "all") && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {searchQuery && (
                  <Badge variant="secondary">
                    Search: "{searchQuery}"
                  </Badge>
                )}
                {selectedCategory !== "all" && (
                  <Badge variant="secondary">
                    Category: {categories.find(c => c.id === selectedCategory)?.name}
                  </Badge>
                )}
                {selectedSubcategory !== "all" && (
                  <Badge variant="secondary">
                    Subcategory: {selectedSubcategory}
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-sm text-muted-foreground">
            Showing {filteredCalculators.length} of {calculators.length} calculators
          </p>
        </div>

        {/* Results Grid */}
        {filteredCalculators.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCalculators.map((calc) => {
              const Icon = calc.icon;
              return (
                <Link key={calc.id} to={calc.href}>
                  <Card className="h-full transition-all hover:shadow-lg hover:-translate-y-1">
                    <CardHeader>
                      <div
                        className={cn(
                          "w-12 h-12 rounded-lg bg-gradient-to-br flex items-center justify-center mb-4",
                          categoryColors[calc.category]
                        )}
                      >
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="flex items-center justify-between">
                        {calc.title}
                      </CardTitle>
                      <CardDescription>{calc.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">{calc.category}</Badge>
                        {calc.subcategory && (
                          <Badge variant="secondary" className="text-xs">
                            {calc.subcategory}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <SearchIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No calculators found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search or filters to find what you're looking for
            </p>
            <Button
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("all");
                setSelectedSubcategory("all");
              }}
            >
              Clear All Filters
            </Button>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
